import { useEffect, useState } from "react";
import { Check, Clock, Armchair } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtTime } from "@/lib/api";

const minutesSince = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 60000);

export function useTick(ms = 30000) {
  const [, set] = useState(0);
  useEffect(() => {
    const t = setInterval(() => set((x) => x + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}

/* Shared ticket card for Dapur & Checker screens */
export default function TicketCard({ bill, prefix, isChecked, canToggle, toggleUrl, doneUrl, doneLabel, onChange, hint }) {
  const [busyId, setBusyId] = useState(null);
  const [busyDone, setBusyDone] = useState(false);
  const pending = bill.items.filter((i) => !isChecked(i));
  const oldest = pending.length ? pending.reduce((a, i) => (i.ordered_at < a ? i.ordered_at : a), pending[0].ordered_at) : bill.updated_at;
  const mins = minutesSince(oldest);
  const urgent = pending.length > 0 && mins >= 15;
  const allDone = bill.items.length > 0 && pending.length === 0;

  const toggle = async (item) => {
    setBusyId(item.id);
    try {
      const r = await api.patch(toggleUrl(item), { value: !isChecked(item) });
      onChange(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };
  const done = async () => {
    setBusyDone(true);
    try {
      await api.post(doneUrl);
      toast.success(`${bill.code} selesai`);
      onChange(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyDone(false);
    }
  };

  return (
    <div data-testid={`${prefix}-ticket-${bill.code}`} className={`gm-rise flex flex-col overflow-hidden rounded-2xl border-2 bg-white shadow-sm ${urgent ? "gm-urgent border-red-400" : allDone ? "border-emerald-400" : "border-stone-200"}`}>
      <div className={`flex items-center justify-between gap-2 px-4 py-3 ${urgent ? "bg-red-50" : "bg-stone-50"}`}>
        <div className="min-w-0">
          <p className="font-mono text-base font-bold">{bill.code}</p>
          <p className="flex items-center gap-1 truncate text-xs text-zinc-600"><Armchair className="h-3.5 w-3.5" />{bill.table_name} · {bill.customer_name}</p>
        </div>
        <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-xs font-bold ${urgent ? "bg-red-600 text-white" : "bg-white text-zinc-600"}`}><Clock className="h-3.5 w-3.5" />{mins}m</span>
      </div>
      <ul className="flex-1 divide-y divide-stone-100">
        {bill.items.map((i) => {
          const checked = isChecked(i);
          const enabled = canToggle(i);
          return (
            <li key={i.id}>
              <button data-testid={`${prefix}-item-${i.id}`} disabled={!enabled || busyId === i.id} onClick={() => toggle(i)}
                className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${checked ? "bg-emerald-50/60" : "hover:bg-orange-50/60"} ${!enabled ? "cursor-not-allowed opacity-50" : ""}`}>
                <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 ${checked ? "border-emerald-600 bg-emerald-600 text-white" : "border-stone-300"}`}>{checked && <Check className="h-4 w-4" />}</span>
                <span className="min-w-0 flex-1">
                  <span className={`block font-semibold ${checked ? "text-zinc-400 line-through" : "text-zinc-900"}`}><span className="font-mono text-orange-700">{i.qty}x</span> {i.name}</span>
                  {i.note && <span className="block text-xs font-medium text-red-600">⟶ {i.note}</span>}
                  <span className="block text-[11px] text-zinc-400">Order #{i.batch} · {fmtTime(i.ordered_at)}{hint ? ` · ${hint(i)}` : ""}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="p-3">
        <Button data-testid={`${prefix}-done-${bill.code}`} disabled={!allDone || busyDone} onClick={done} className="h-11 w-full rounded-xl bg-emerald-600 text-base font-semibold hover:bg-emerald-700 active:scale-95 transition-transform">
          {doneLabel}
        </Button>
      </div>
    </div>
  );
}
